import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Api from "../../services/Api";
import PageHeader from "../../components/PageHeader";
import PolicyUploader from "./PolicyUploader";
import AppDialog from "../../components/Dialog";
import { downloadFile, removeTerms } from "../../services/termsOfUse";
import Confirm from "../../components/Dialog/Confirm";
import dayjs from "dayjs";
import { ITermsOfUse } from "@/types/TermsOfUse";

const TermsOfUse = ({ apiLink }: { apiLink: string }) => {
  const [uploaderView, setUploaderView] = useState<"add" | "edit" | null>(null);
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<number>();
  const [pdfFile, setPdfFile] = useState<string>();
  const [selectedPolicy, setSelectedPolicy] = useState<ITermsOfUse | null>(
    null
  );
  const {
    data,
    isLoading,
    isError,
    refetch: refetchPolicies,
  } = useQuery("termsOfUse", () => Api.getTableData(apiLink, localStorage.getItem("DP_PROJECT_ID")));
  const { mutateAsync } = useMutation("removeTerms", removeTerms);
  const {
    data: file,
    isLoading: fetchingFile,
    isError: FileError,
    refetch,
  } = useQuery("TOUDownload", () => downloadFile(selectedPolicy?.id!), {
    enabled: !!selectedPolicy?.id,
    cacheTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (selectedPolicy?.id) {
      // getTableData()
      refetch().then((res) => {
        if (res.data?.data) {
          // External link
          setIsViewerVisible(false);
          window.open(res.data.data.terms_of_use_url, "_blank");
        } else {
          // const data = await downloadFile(selectedPolicy?.id);
          // console.log(res);
          const imgUrl = URL.createObjectURL(res.data);
          setPdfFile(imgUrl);
          if (res.data.type !== "application/pdf") {
            setIsViewerVisible(false); // File will be downloaded if not pdf
          }
        }
      });
    }
  }, [selectedPolicy]);

  const previewFile = (policy: ITermsOfUse) => {
    setSelectedPolicy(policy);
    setIsViewerVisible(true);
  };

  const deletePolicy = () => {
    mutateAsync(policyToDelete!).then(() => refetchPolicies());
  };

  return (
    <div>
      <PageHeader
        title="Terms Of Use"
        buttonLabel="Add Terms Of Use"
        onButtonClick={() => setUploaderView("add")}
      />
      {uploaderView !== null && (
        <PolicyUploader
          mode={uploaderView}
          selectedPolicy={selectedPolicy}
          onClose={() => setUploaderView(null)}
          refetch={refetchPolicies}
        />
      )}
      <div className="grid grid-cols-12 gap-6">
        {data?.data?.data?.results?.map((terms: ITermsOfUse) => (
          <div
            key={terms.id}
            role="button"
            onClick={() => {
              previewFile(terms);
            }}
            className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
          >
            <figure className="bg-white">
              <img
                style={{ height: '50px', width: 'auto', border: 'none', marginLeft: '20px' }}
                src={terms.project.logo_url}
                alt=""
                className="w-full border"
              />
              <figcaption className="text-left p-4">
                <div className="flex items-center justify-between">
                  <h4 className="mb-3">{terms.terms_of_use_text}</h4>
                  <div className="flex items-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPolicy(terms);
                        setUploaderView("edit");
                      }}
                    >
                      <PencilIcon className="h-4 w-4 mr-2 text-purple-1 hover:text-purple-800" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPolicyToDelete(terms.id);
                      }}
                    >
                      <TrashIcon className="h-4 w-4 text-red-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
                <h5 className="text-grey-1 mb-3">
                  Version - {terms.terms_of_use_version}
                </h5>
                <h5 className="text-grey-1">
                  Last Updated -{" "}
                  {dayjs(terms.terms_of_use_date).format("MMM DD, YYYY")}
                </h5>
              </figcaption>
            </figure>
          </div>
        ))}
      </div>
      <AppDialog onClose={setIsViewerVisible} isOpen={isViewerVisible}>
        {selectedPolicy && pdfFile && (
          <iframe
            src={`${pdfFile}`}
            title="displayFile"
            width="100%"
            height="99%"
            style={{ borderStyle: "solid", minHeight: "80vh" }}
          />
          // <DocViewer
          //   documents={[{ uri: pdfFile }]}
          //   pluginRenderers={DocViewerRenderers}
          //   config={{
          //    noRenderer: {
          //     overrideComponent: MyNoRenderer,
          //     },
          //   }}
          // />
        )}
      </AppDialog>
      <Confirm
        title="Delete Policy?"
        description="Are you sure you want to delete this policy?"
        isOpen={Boolean(policyToDelete)}
        onClose={() => setPolicyToDelete(undefined)}
        onProceed={deletePolicy}
      />
    </div>
  );
};

export default TermsOfUse;
